import type { IAlgoliaSearch } from '@readme/react/src/Hub/Search/types';

import loadable from '@loadable/component';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import SearchTrigger from '@Hub/Search/Trigger';

import { AppMetaContext, ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useDeferForHydrationMismatch from '@core/hooks/useDeferForHydrationMismatch';
import useMeta from '@core/hooks/useMeta';
import classy from '@core/utils/classy';

import KbdShortcut from '@ui/KbdShortcut';

import classes from '../../style.module.scss';

/**
 * Code-splitting SearchBert to avoid loading its heavy dependencies when
 * a project doesn't have it enabled.
 */
const SearchBert = loadable(() => import('@Hub/SearchBert'), {
  ssr: true,
});

interface SearchProps {
  isModalOpen: boolean;

  /**
   * check for main search input in modern layout
   */
  isPrimary?: boolean;
  search: IAlgoliaSearch;
  toggleSearch: () => void;
}

const Search = ({ isPrimary = false, toggleSearch, ...props }: SearchProps) => {
  const appMeta = useContext(AppMetaContext);
  const { project } = useContext(ProjectContext);
  const { appearance } = project;
  const { pathname, search } = useLocation();
  const bem = useClassy(classes, 'Header');
  const { t } = useTranslation();

  const meta = useMeta('K');
  const shortcut = useDeferForHydrationMismatch(meta, '');

  const onLandingPageWithSearch =
    (appMeta.type === 'home' || pathname === '/') && appearance?.promos?.[0]?.extras?.type === 'search';

  const hasCustomBackground = appearance.header.style === 'custom' && appearance.header.img.length;
  const projectLayout = appearance.layout.style;
  const additionalSearchClasses = classy(
    !(appearance.header.style === 'gradient' || hasCustomBackground) &&
      bem('rm-Header-search_feather', '-search_feather'),
    isPrimary && '-search_primary',
  );

  const owlbotTrial = new URLSearchParams(search).get('owlbotTrial');
  // This is old experience so newExperience should be false to use it
  const owlbotEnabled = (project.owlbot?.enabled && !project.owlbot?.newExperience) || owlbotTrial === 'true';

  // Checking there is a valid searchApiKey cause the page crashes otherwise
  if (!onLandingPageWithSearch && !!props.search.searchApiKey && !owlbotEnabled) {
    return (
      <SearchTrigger
        className={bem('rm-Header-search', '-search', `-search_${projectLayout}`, additionalSearchClasses)}
        toggle={{
          className: bem('rm-SearchToggle', isPrimary && 'rm-SearchToggle_primary'),
          'data-symbol': `${shortcut.slice(0, -1)}`,
        }}
        toggleSearch={toggleSearch}
      >
        <div className="rm-SearchToggle-icon icon-search1" />
        <div className={bem('-search-placeholder', 'rm-SearchToggle-placeholder')}>{t('search.placeholder')}</div>
        <KbdShortcut className="rm-SearchToggle-shortcut">{shortcut}</KbdShortcut>
      </SearchTrigger>
    );
  } else if (!!owlbotEnabled && !!props.search.searchApiKey) {
    return (
      <div
        className={bem(
          'rm-Header-search',
          '-search',
          `-search_${projectLayout}`,
          '-search_owlbot',
          additionalSearchClasses,
        )}
      >
        <SearchBert
          childSubdomain={project.parent ? project.subdomain : undefined}
          isFullWidth={isPrimary}
          toggleSearch={toggleSearch}
          {...props}
        />
      </div>
    );
  }
  return null;
};

export default Search;
